import { useEffect, useState } from 'react'
import { useAuth } from './auth'
import { fetchMySubscription, type SubscriptionStatus } from './api/subscriptions'
import { hasActiveEntitlement } from './api/iap'

const NONE: SubscriptionStatus = {
  isActive: false, tier: null, status: null, source: null, currentPeriodEnd: null,
  stampGrants: null, storePeriodEnd: null,
}

// ── In-session refresh signal ────────────────────────────────────────────
// The hook used to fetch exactly once per session-id, so a user who had
// JUST purchased (or restored) kept seeing paywalls until an app restart.
// Any code that changes entitlement calls refreshSubscriptionStatus() and
// every mounted useSubscription() refetches. A single module-level
// foreground listener also refreshes when the app resumes — subscription
// state can change server-side while the app is suspended (renewal,
// cancellation, web purchase).
let refreshTick = 0
const refreshListeners = new Set<() => void>()

export function refreshSubscriptionStatus(): void {
  refreshTick++
  refreshListeners.forEach(l => l())
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') refreshSubscriptionStatus()
  })
}

/**
 * Subscription status for the signed-in user, refetched whenever the
 * session changes or refreshSubscriptionStatus() fires (post-purchase,
 * post-restore, app foreground). Guests (no session) are always NONE.
 */
export function useSubscription(): SubscriptionStatus & { loading: boolean } {
  const { session } = useAuth()
  const [status, setStatus] = useState<SubscriptionStatus>(NONE)
  const [loading, setLoading] = useState(false)
  const [tick, setTick] = useState(refreshTick)

  useEffect(() => {
    const onRefresh = () => setTick(refreshTick)
    refreshListeners.add(onRefresh)
    return () => { refreshListeners.delete(onRefresh) }
  }, [])

  useEffect(() => {
    if (!session) { setStatus(NONE); return }
    let cancelled = false
    setLoading(true)
    // Server (Supabase) is the source of truth, but it needs network.
    // On native iOS the RevenueCat SDK caches the entitlement locally,
    // so fall back to it when the server says "no" or can't be reached.
    // This keeps offline-capable paid features (Phone Pass Play) working
    // for a subscriber who's gone offline. On web hasActiveEntitlement()
    // is always false, so behaviour there is unchanged.
    Promise.all([
      fetchMySubscription().catch(() => NONE),
      hasActiveEntitlement().catch(() => false),
    ])
      .then(([server, rcActive]) => {
        if (cancelled) return
        if (!server.isActive && rcActive) {
          setStatus({
            ...server,
            isActive: true,
            status: server.status ?? 'active',
            source: server.source ?? 'apple',
          })
        } else {
          setStatus(server)
        }
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id, tick])

  return { ...status, loading }
}

/**
 * Wraps an action so it only runs for active subscribers; otherwise
 * routes the user to the paywall. Use at call sites that trigger
 * paid features (online matchmaking, hosting, AI opponents, weekly-
 * event submit, Phone Pass Play). The only free path is joining an
 * existing room by code, which should NOT use this gate.
 *
 * Usage:
 *   const gate = useGatedAction(onOpenPaywall)
 *   <button onClick={() => gate(onOnlineMatch)}>Online Match</button>
 */
export function useGatedAction(onOpenPaywall: () => void) {
  const sub = useSubscription()
  return (action: () => void) => {
    // While the subscription state is resolving the tap does nothing:
    // a second tap a moment later works. The old behavior let the tap
    // through during the loading window, which opened paid surfaces
    // to fast-tapping free users (the server did not verify weekly
    // writes until 2026-08-31, so those slipped all the way in).
    if (sub.loading) return
    if (sub.isActive) { action(); return }
    onOpenPaywall()
  }
}
